import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { poiService, POI, Card } from '../services/poiService';
import { useLanguage } from '../contexts/LanguageContext';
import RichTextDisplay from '../components/RichTextDisplay';
import { getReadableCategoryName } from '../utils/categoryUtils';
import LandingLanguagePicker from '../components/LandingLanguagePicker';
import GetAppBar from '../components/GetAppBar';

const POILandingPage: React.FC = () => {
  const { poiUuid } = useParams<{ poiUuid: string }>();
  const navigate = useNavigate();
  const { selectedLanguage, isLoading: languageLoading } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [poi, setPoi] = useState<POI | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (languageLoading) return;

    const fetchData = async () => {
      if (!poiUuid) {
        setError('POI UUID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const poiResponse = await poiService.fetchPOIById(poiUuid);
        setPoi(poiResponse.data);

        const cardsResponse = await poiService.fetchCardsByPOI(poiUuid, selectedLanguage?.code ?? undefined);
        setCards(cardsResponse.data);

        if (cardsResponse.data.length === 0) {
          setError('No cards available for this location');
        }
      } catch (err) {
        console.error('Error fetching POI data:', err);
        setError('Failed to load POI details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [poiUuid, selectedLanguage, languageLoading]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !poi) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <svg className="w-20 h-20 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Location Not Found</h1>
          <p className="text-gray-400 mb-8">{error || 'This location could not be loaded'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Explore Other Places
          </button>
        </div>
      </div>
    );
  }

  // Use first card's image_url if available, otherwise fall back to POI photo endpoint
  const photoUrl = cards.length > 0 && cards[0].image_url 
    ? cards[0].image_url 
    : `https://api.blip-platform.com/api/photo?poi_uuid=${poiUuid}`;

  return (
    <div ref={scrollContainerRef} className="bg-black h-screen overflow-y-scroll snap-y snap-mandatory">
      <GetAppBar scrollContainerRef={scrollContainerRef} />
      {/* Fixed header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent px-4 pt-4 pb-10">
        <div className="flex items-start justify-between max-w-3xl mx-auto">
          <div>
            <h1 className="text-white text-lg font-semibold drop-shadow-lg leading-snug max-w-xs md:max-w-lg">
              {poi.name}
            </h1>
            <div className="flex items-center gap-2 text-white/70 text-sm mt-1">
              {poi.category && (
                <>
                  <span className="text-xs font-medium">{getReadableCategoryName(poi.category)}</span>
                  <span className="text-white/30">·</span>
                </>
              )}
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{poi.city || 'Unknown City'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            <LandingLanguagePicker />
            <button
              onClick={() => navigate('/')}
              className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Hero section — full viewport photo */}
      <section className="h-screen relative snap-start">
        <img
          src={photoUrl}
          alt={poi.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-0 right-0 flex flex-col items-center gap-2 text-white/60">
          <span className="text-xs tracking-widest uppercase font-medium">Scroll to explore</span>
          <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* Card sections */}
      {cards.map((card, index) => (
        <CardScrollSection key={card.id_v2 || index} card={card} index={index} total={cards.length} />
      ))}

      {/* Continue exploring CTA */}
      <section className="h-screen bg-black px-6 flex items-center justify-center snap-start">
        <div className="max-w-md mx-auto text-center">
          <h3 className="text-white text-xl font-semibold mb-3">Continue exploring</h3>
          <p className="text-white/50 text-sm mb-10">
            Download Blip to discover amazing places as you explore
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            {import.meta.env.VITE_APPLE_STORE_URL && (
              <a
                href={import.meta.env.VITE_APPLE_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img src="/images/badges/app-store-badge.svg" alt="Download on the App Store" className="h-12" />
              </a>
            )}
            {import.meta.env.VITE_PLAY_STORE_URL && (
              <a
                href={import.meta.env.VITE_PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img src="/images/badges/google-play-badge.svg" alt="Get it on Google Play" className="h-12" />
              </a>
            )}
          </div>

          {/* Social media */}
          <div className="mt-12">
            <p className="text-white/30 text-xs tracking-widest uppercase mb-4">Follow us on</p>
            <div className="flex items-center justify-center gap-6">
              <a
                href="https://www.instagram.com/blip.travel/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-white/50 hover:text-white transition-colors"
              >
                {/* Instagram */}
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/people/Blip-the-travel-app/61582985165794/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-white/50 hover:text-white transition-colors"
              >
                {/* Facebook */}
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@BlipApp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-white/50 hover:text-white transition-colors"
              >
                {/* YouTube */}
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CardScrollSection: React.FC<{ card: Card; index: number; total: number }> = ({ card, index, total }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={`h-screen flex items-center overflow-y-auto py-24 px-6 snap-start ${index % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900'}`}>
      <div
        ref={ref}
        className={`max-w-2xl mx-auto w-full transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="text-white/25 text-xs font-semibold tracking-widest uppercase mb-8">
          {index + 1} / {total}
        </div>
        {card.title && (
          <h2 className="text-white text-3xl md:text-4xl font-bold mb-2 leading-tight">
            {card.title}
          </h2>
        )}
        {card.subtitle && (
          <p className="text-white/45 text-lg mb-8">{card.subtitle}</p>
        )}
        {card.image_url && (
          <div className="mb-8">
            <img 
              src={card.image_url} 
              alt={card.title || 'Card image'}
              className="w-full max-h-[50vh] object-cover rounded-lg"
              loading="lazy"
              crossOrigin="anonymous"
              referrerPolicy="strict-origin-when-cross-origin"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        {card.text && (
          <RichTextDisplay
            content={card.text}
            className="text-white/80 text-lg md:text-xl leading-relaxed"
            enableShowMore={false}
          />
        )}
        {card.audio_url && (
          <div className="mt-8">
            <audio controls className="w-full max-w-md">
              <source src={card.audio_url} type="audio/mpeg" />
            </audio>
          </div>
        )}
      </div>
    </section>
  );
};

export default POILandingPage;
